import { TrashIcon } from "@heroicons/react/24/outline";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import PageHeading from "../../../components/common/PageHeading";
import { Text } from "../../../components/common/Typography";
import Button from "../../../components/form/Button";
import Input from "../../../components/form/Input";
import { ROUTE_DESTINATIONS } from "../../../constants/routes";
import {
  DestinationStatusEnum,
  DestinationTabs,
  DestinationTypeEnum,
} from "../../../enums/destination";
import { WordPressDestination } from "../../../models/destination";
import TemplateContainer from "../../TemplateForm/components/common/TemplateContainer";
import { mask, secretKeyMask } from "../Destination";
import PostDelayHourSelector from "../components/common/PostDelayHourSelector";
import RandPostHourSelector from "../components/common/RandPostHourSelector";
import { StatusSelector } from "../components/common/StatusSelector";
import DestinationSaveModal from "../components/modals/DestinationSaveModal";
import WordPressGuideModal from "../components/modals/WordPressGuideModal";

interface WordpressProps {
  destination: any;
  isTestLoading: boolean;
  isTestError: boolean;
  testErrorMessage: any;
  showSaveModal: boolean;
  testSuccessMessage: string;
  isSaving: boolean;
  onSaveSuccess: () => void;
  handleTest: (formValues: any) => void;
  handleDelete: () => void;
  handleSubmit: (formValues: any) => void;
}

const wordpressSchema = Yup.object().shape({
  name: Yup.string().required("Destination name is required"),
  destination: Yup.object().shape({
    url: Yup.string().required("Wordpress URL is required"),
    username: Yup.string().required("Admin Login Username is required"),
    secret_key: Yup.string()
      .required("App password is required")
      .test(
        "no-asterisk",
        "Please verify your app password",
        (value) => !value || !value.includes(secretKeyMask)
      ),
    post_type: Yup.string()
      .nullable()
      .max(20, "Custom post type slug must be at most 20 characters long")
      .matches(
        /^[a-z0-9_-]+$/,
        "Custom post type slug may only contain lowercase letters, numbers, dashes, and underscores"
      ),
  }),
});

const initialValues: WordPressDestination = {
  name: "",
  destination_type_id: DestinationTypeEnum.WORDPRESS,
  destination: {
    url: "",
    username: "",
    author_id: null,
    category_id: null,
    secret_key: "",
    status: DestinationStatusEnum.DRAFT,
    post_type: "",
    random_post_hours: null,
    post_delay_hours: null,
  },
};

const WordPress: React.FC<WordpressProps> = ({
  destination,
  isTestLoading,
  isTestError,
  testErrorMessage,
  showSaveModal,
  testSuccessMessage,
  isSaving,
  onSaveSuccess,
  handleTest,
  handleSubmit,
  handleDelete,
}) => {
  const navigate = useNavigate();

  const [formValues, setFormValues] =
    useState<WordPressDestination>(initialValues);
  const [passwordGuide, setPasswordGuide] = useState(false);

  useEffect(() => {
    if (destination) {
      setFormValues(
        new WordPressDestination({
          ...destination,
          destination: {
            ...destination.destination,
            secret_key: `${secretKeyMask}${destination.destination.secret_key}`,
          },
        })
      );
    }
  }, [destination]);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(ROUTE_DESTINATIONS + `?tab=${DestinationTabs.ADDED}`);
    }
  };

  return (
    <Formik
      initialValues={formValues}
      enableReinitialize={true}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={(values: WordPressDestination) => {
        if (values?.destination?.url) {
          values["destination"]["url"] = values?.destination?.url?.replace(
            /\/+$/,
            ""
          );
        }
        handleSubmit(values);
      }}
      validationSchema={wordpressSchema}
    >
      {({ values, errors, validateForm, setFieldValue }) => {
        return (
          <Form
            className="flex flex-col gap-6 flex-wrap transition-all duration-200"
            noValidate
          >
            <TemplateContainer
              className="max-w-[60.5rem] rounded"
              style={{ padding: 0 }}
            >
              <div className="py-4 px-4 sm:px-6 relative bg-gray-50 rounded-t-lg">
                <PageHeading heading="Configure WordPress" />
                <Text className="text-gray-500 text-sm">
                  Provide your account details to establish a connection
                </Text>
                {formValues?.destination_id && (
                  <Button
                    variant="light-danger"
                    tooltip="Delete wordpress connection"
                    className="group hover:border-red-500 hover:bg-red-50 absolute top-4 right-3"
                    onClick={handleDelete}
                  >
                    <TrashIcon className="size-5 text-red-500" />
                  </Button>
                )}
              </div>
              <div className="bg-gray-200 h-px w-full" />
              <div className="py-4 px-4 sm:px-6 flex flex-col gap-8 mb-4">
                <div className="flex flex-col md:flex-row gap-6">
                  <Input
                    id="name"
                    label="Destination Name"
                    name="name"
                    type="text"
                    required
                    placeholder="Name your destination"
                    className="max-w-md"
                    tooltip="Name your destination (e.g., My WordPress Site)"
                    value={values.name}
                    errorMessage={errors?.name}
                    errorPosition="bottom-left"
                    onChange={(e: any) => setFieldValue("name", e.target.value)}
                  />

                  <Input
                    id="url"
                    label="URL"
                    name="destination.url"
                    type="text"
                    required
                    placeholder="https://mydomain.com"
                    className="max-w-md"
                    tooltip="The root domain for your WordPress Site (e.g. https://mydomain.com)"
                    value={values.destination.url}
                    errorMessage={errors?.destination?.url}
                    errorPosition="bottom-left"
                    onChange={(e: any) =>
                      setFieldValue("destination.url", e.target.value)
                    }
                  />
                </div>
                <div className="flex flex-col md:flex-row gap-6">
                  <Input
                    id="username"
                    label="Admin Login Username"
                    name="destination.username"
                    type="text"
                    required
                    placeholder="Your WordPress username"
                    className="max-w-md"
                    tooltip="Your WordPress username"
                    value={values.destination.username}
                    errorMessage={errors?.destination?.username}
                    errorPosition="bottom-left"
                    onChange={(e: any) =>
                      setFieldValue("destination.username", e.target.value)
                    }
                  />
                  <div className="relative w-full">
                    <Input
                      id="secret_key"
                      label="App Password"
                      name="secret_key"
                      type="text"
                      required
                      placeholder="App Password (Not your WordPress login password)"
                      className="max-w-md"
                      tooltip="IMPORTANT: This is not your WordPress login password, rather a separate password generated for API usage."
                      value={values.destination.secret_key}
                      errorMessage={errors?.destination?.secret_key}
                      errorPosition="bottom-left"
                      fontMono
                      onChange={(e: any) => {
                        setFieldValue("destination.secret_key", e.target.value);
                      }}
                      onFocus={(e: any) => {
                        if (e.target.value.includes(mask)) {
                          setFieldValue("destination.secret_key", "");
                        }
                      }}
                    />
                    <Button
                      paddingZero
                      type="button"
                      variant="transparent-primary"
                      className="absolute mt-1"
                      onClick={() => setPasswordGuide(!passwordGuide)}
                    >
                      How to generate
                    </Button>
                  </div>
                </div>
                {passwordGuide && (
                  <WordPressGuideModal
                    onRequestClose={() => setPasswordGuide(false)}
                  />
                )}

                <div className="flex flex-col md:flex-row gap-6">
                  <Input
                    id="author_id"
                    label="Author ID"
                    name="destination.author_id"
                    type="number"
                    tooltip='This is the WordPress Author ID (a number) to whom you want the posts associated with. You can access this ID using a WordPress plugin such as "Reveal IDs."'
                    placeholder="Optional. WordPress Author ID (a number)"
                    className="max-w-md"
                    value={values?.destination?.author_id || ""}
                    errorMessage={errors?.destination?.author_id}
                    errorPosition="bottom-left"
                    onChange={(e: any) =>
                      setFieldValue("destination.author_id", e.target.value)
                    }
                  />

                  <Input
                    id="category_id"
                    label="Category ID"
                    name="destination.category_id"
                    type="number"
                    tooltip='This is the WordPress Category ID (a number) to assign your posts a category. You can access this ID using a WordPress plugin such as "Reveal IDs."'
                    placeholder="Optional. WordPress Category ID (a number)"
                    className="max-w-md"
                    value={values?.destination?.category_id || ""}
                    errorMessage={errors?.destination?.category_id}
                    errorPosition="bottom-left"
                    onChange={(e: any) =>
                      setFieldValue("destination.category_id", e.target.value)
                    }
                  />
                </div>
                <Input
                  id="post_type"
                  label="Custom post type slug"
                  name="destination.post_type"
                  type="text"
                  maxLength={20}
                  placeholder='Optional. Leave blank if you are unsure and articles will be posted to the default "posts"'
                  tooltip='Use the post type slug.  A custom post type in WordPress is a way to create content types that are different from standard posts and pages. The default post type for posting blog articles is "posts".'
                  value={values.destination.post_type ?? ""}
                  errorMessage={errors?.destination?.post_type}
                  errorPosition="bottom-left"
                  onChange={(e: any) => {
                    let inputValue = e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9_-]/g, "")
                      .slice(0, 20);
                    setFieldValue("destination.post_type", inputValue);
                  }}
                />
                <StatusSelector
                  status={values?.destination.status}
                  onChange={(status: string) =>
                    setFieldValue("destination.status", status)
                  }
                  platform={DestinationTypeEnum.WORDPRESS}
                />
                {values?.destination.status ===
                  DestinationStatusEnum.FUTURE && (
                  <div className="flex flex-col gap-8 ml-6">
                    <PostDelayHourSelector
                      postDelayHours={values?.destination?.post_delay_hours}
                      onChange={(value: number | null) =>
                        setFieldValue("destination.post_delay_hours", value)
                      }
                    />
                    <RandPostHourSelector
                      randomPostHours={values?.destination?.random_post_hours}
                      onChange={(value: number | null) =>
                        setFieldValue("destination.random_post_hours", value)
                      }
                    />
                  </div>
                )}
              </div>
            </TemplateContainer>
            {isTestError && (
              <div className="text-red-500 text-sm my-1">
                {testErrorMessage?.data?.detail ??
                  "Wordpress plugins or security may be interfering with your site connection. Please check your settings."}
              </div>
            )}
            <div className="flex items-center justify-between max-w-5xl">
              <div className="flex items-center gap-6">
                <Button
                  variant="primary"
                  className="min-w-[8.75rem]"
                  onClick={() => {
                    validateForm().then((formErrors) => {
                      if (Object.keys(formErrors).length === 0) {
                        handleTest(values);
                      }
                    });
                  }}
                  loading={isTestLoading}
                >
                  Test Connection
                </Button>
                <Button
                  variant="transparent"
                  onClick={handleGoBack}
                >
                  Cancel
                </Button>
              </div>
            </div>
            {showSaveModal && (
              <DestinationSaveModal
                message={testSuccessMessage}
                loading={isSaving}
                onSave={() => handleSubmit(values)}
                onRequestClose={onSaveSuccess}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default WordPress;
